import { I18n } from "@assets/strings";
import React from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { CourseSectionType } from "@domain/lms/course/courseSection";
import { BigPlusButton } from "@ui/components/buttons/bigPlusButton";
import {
	AssetManagerIcon,
	CameraIcon,
	FileIcon,
	ImageIcon,
	PdfIcon,
	QCMIcon,
	SubtitleIcon,
	TextIcon,
	YoutubeIcon,
} from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { AssetBrowserModal } from "@ui/components/media/assetBrowser/assetBrowserModal";
import { FileUpload } from "@ui/components/mediaEditor/fileUpload";
import { Menu } from "@ui/components/menus/menu";
import { useModalTools } from "@ui/modals/modalContext";
import { gap } from "@ui/styles/utils";
import {
	useChapterController,
	useEditorController,
} from "@ui/pages/lms/courseEditorPage/courseEditorController/editorControllerContext";

interface AddSectionMenuProps extends React.HTMLAttributes<HTMLDivElement> {
	currentlyEditing: boolean;
	asFirst: boolean;
}
export const AddSectionMenu: React.FC<AddSectionMenuProps> = ({ currentlyEditing, asFirst, ...htmlProps }) => {
	const chapterController = useChapterController();
	const courseController = useEditorController();
	const { trackingService } = useServices();
	const { createModal } = useModalTools();

	const trackSection = (type: CourseSectionType | "QCM" | "YOUTUBE"): void => {
		trackingService.track("course / add section", { type });
	};

	return (
		<Menu
			{...htmlProps}
			closeOnSelect
			renderMenuContent={(close) => (
				<SectionTypes>
					<SectionRow
						onClick={() => {
							chapterController.addSection("SUBTITLE");
							trackSection("SUBTITLE");
						}}
					>
						<SubtitleIcon /> <div>{I18n.t("course.editor.section.subtitle.new")}</div>
					</SectionRow>
					<SectionRow
						onClick={() => {
							chapterController.addSection("TEXT");
							trackSection("TEXT");
						}}
					>
						<TextIcon /> <div>{I18n.t("course.editor.section.text.new")}</div>
					</SectionRow>
					<Separator />
					<SectionRow
						onClick={() => {
							createModal((closeModal) => (
								<AssetBrowserModal
									close={(assets) => {
										closeModal();
										const asset = assets?.get(0);
										if (asset) {
											if (asset.mimeType.match("image/*")) {
												chapterController.addSection("IMAGE", asset.id);
												courseController.addAsset(asset);
												trackSection("IMAGE");
											} else if (asset.mimeType.match("video/*")) {
												chapterController.addSection("VIDEO", asset.id);
												courseController.addAsset(asset);
												trackSection("VIDEO");
											} else if (asset.mimeType === "application/pdf") {
												chapterController.addSection("PDF", asset.id);
												courseController.addAsset(asset);
												trackSection("PDF");
											} else {
												chapterController.addSection("ATTACHMENT", asset.id, asset.displayName);
												courseController.addAsset(asset);
												trackSection("ATTACHMENT");
											}
										}
									}}
								/>
							));
						}}
					>
						<AssetManagerIcon /> <div>{I18n.t("asset_manager.title")}</div>
					</SectionRow>
					<Separator />
					<SectionRow onClick={(e) => e.stopPropagation()}>
						<FileUpload
							onUpload={(media) => {
								chapterController.addSection("IMAGE", media.id);
								courseController.addAsset(media);
								trackSection("IMAGE");
								close();
							}}
						>
							{(_, loading) => (
								<ImageUploadContent>
									{loading ? <ClipLoader size={16} /> : <ImageIcon />}{" "}
									<div>{I18n.t("course.editor.section.image.new")}</div>
								</ImageUploadContent>
							)}
						</FileUpload>
					</SectionRow>
					<SectionRow onClick={(e) => e.stopPropagation()}>
						<FileUpload
							onUpload={(media) => {
								chapterController.addSection("PDF", media.id);
								courseController.addAsset(media);
								trackSection("PDF");
								close();
							}}
							type="pdf"
						>
							{(_, loading) => (
								<ImageUploadContent>
									{loading ? <ClipLoader size={16} /> : <PdfIcon />}{" "}
									<div>{I18n.t("course.editor.section.document.new")}</div>
								</ImageUploadContent>
							)}
						</FileUpload>
					</SectionRow>
					<SectionRow
						onClick={(e) => {
							e.stopPropagation();
						}}
					>
						<FileUpload
							type="video"
							onUploadStart={(taskId) => {
								close();
								chapterController.addLoadingSection(taskId);
							}}
							onUpload={(media, _, taskId) => {
								chapterController.addOrReplaceSection("VIDEO", media.id, taskId);
								courseController.addAsset(media);
								trackSection("VIDEO");
							}}
						>
							{() => (
								<ImageUploadContent>
									<CameraIcon /> <div>{I18n.t("course.editor.section.video.new")}</div>
								</ImageUploadContent>
							)}
						</FileUpload>
					</SectionRow>
					<SectionRow onClick={(e) => e.stopPropagation()}>
						<FileUpload
							type="file"
							onUpload={(media, fileName) => {
								chapterController.addSection("ATTACHMENT", media.id, fileName);
								courseController.addAsset(media);
								trackSection("ATTACHMENT");
								close();
							}}
						>
							{(_, loading) => (
								<ImageUploadContent>
									{loading ? <ClipLoader size={16} /> : <FileIcon />}{" "}
									<div>{I18n.t("course.editor.section.attachment.new")}</div>
								</ImageUploadContent>
							)}
						</FileUpload>
					</SectionRow>
					<Separator />
					<SectionRow
						onClick={() => {
							chapterController.addSection("VIDEO");
							trackSection("YOUTUBE");
						}}
					>
						<YoutubeIcon /> <div>{I18n.t("course.editor.section.youtube.new")}</div>
					</SectionRow>
					<Separator />
					{!currentlyEditing && (
						<SectionRow
							onClick={() => {
								chapterController.addExercise();
								trackSection("QCM");
							}}
						>
							<QCMIcon /> <div>{I18n.t("course.editor.section.qcm.new")}</div>
						</SectionRow>
					)}
				</SectionTypes>
			)}
			menuWrapperStyle={{
				left: "50%",
				right: "initial",
				top: "50%",
				transform: "translate(-50%, 16px)",
			}}
		>
			<BigPlusButton label={I18n.t("course.editor.section.add") + (asFirst ? "*" : "")} />
		</Menu>
	);
};

const SectionTypes = styled.div`
	${gap(16)}
	min-width: 200px;
`;

const SectionRow = styled.div`
	cursor: pointer;
	display: flex;
	align-items: center;
	${({ theme }) => theme.textStyles.text};
	div {
		transition: transform 0.2s ease-in-out;
	}
	&:hover div:not(:first-child) {
		transform: translateX(5px);
	}
	svg {
		margin-right: 5px;
		width: 18px;
	}
`;

const ImageUploadContent = styled.div`
	${row(5, "flex-start", "center")};
`;

const Separator = styled.div`
	height: 1px;
	background-color: ${({ theme }) => theme.colors.lightergray};
`;
